import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { AdminLayout } from './components/AdminLayout';
import { API_BASE_URL } from '../../config/apiConfig';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const PageTitle = styled.h1`
  margin: 0;
  color: #2c3e50;
  font-size: 28px;
`;

const Button = styled.button`
  padding: 10px 16px;
  background-color: ${props => {
    if (props.variant === 'danger') return '#e74c3c';
    if (props.variant === 'success') return '#27ae60';
    if (props.variant === 'muted') return '#7f8c8d';
    return '#3498db';
  }};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;

  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
  }
`;

const Card = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 1fr) 160px 120px;
  gap: 15px;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #34495e;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #dfe6e9;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  color: #34495e;
  border-bottom: 2px solid #ecf0f1;
  font-size: 13px;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #ecf0f1;
  color: #2c3e50;
  vertical-align: top;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 999px;
  background-color: ${props => {
    if (props.status === 'success') return '#d4edda';
    if (props.status === 'partial_success') return '#fff3cd';
    if (props.status === 'error') return '#f8d7da';
    return '#ecf0f1';
  }};
  color: ${props => {
    if (props.status === 'success') return '#155724';
    if (props.status === 'partial_success') return '#856404';
    if (props.status === 'error') return '#721c24';
    return '#34495e';
  }};
  font-size: 12px;
`;

const Message = styled.div`
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  background-color: ${props => props.type === 'error' ? '#f8d7da' : '#d4edda'};
  color: ${props => props.type === 'error' ? '#721c24' : '#155724'};
  border: 1px solid ${props => props.type === 'error' ? '#f5c6cb' : '#c3e6cb'};
`;

const EmptyState = styled.div`
  padding: 30px;
  color: #7f8c8d;
  text-align: center;
`;

const Summary = styled.div`
  font-size: 12px;
  color: #7f8c8d;
  line-height: 1.5;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #34495e;
`;

const initialForm = {
  sheetUrl: '',
  discountPercent: '',
  isActive: true
};

export function GoogleSheetImports() {
  const [imports, setImports] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const token = sessionStorage.getItem('admin-token');

  const request = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        Authorization: `${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {})
      }
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || data.error || 'Request failed');
    }
    return data;
  }, [token]);

  const fetchImports = useCallback(async () => {
    try {
      setLoading(true);
      const data = await request('/admin/google-sheet-imports');
      setImports(data.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    fetchImports();
  }, [fetchImports]);

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setActionLoading(true);
      const payload = {
        sheetUrl: form.sheetUrl,
        discountPercent: Number(form.discountPercent),
        isActive: form.isActive
      };

      if (editingId) {
        await request(`/admin/google-sheet-imports/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        setMessage({ type: 'success', text: 'Sheet import updated.' });
      } else {
        await request('/admin/google-sheet-imports', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        setMessage({ type: 'success', text: 'Sheet import added.' });
      }

      resetForm();
      await fetchImports();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      sheetUrl: item.sheetUrl || '',
      discountPercent: item.discountPercent || '',
      isActive: item.isActive !== false
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this Google Sheet import?')) return;

    try {
      setActionLoading(true);
      await request(`/admin/google-sheet-imports/${id}`, { method: 'DELETE' });
      setMessage({ type: 'success', text: 'Sheet import deleted.' });
      await fetchImports();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSync = async (id) => {
    try {
      setActionLoading(true);
      await request(`/admin/google-sheet-imports/${id}/sync`, { method: 'POST' });
      setMessage({ type: 'success', text: 'Sheet import synced.' });
      await fetchImports();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSyncAll = async () => {
    try {
      setActionLoading(true);
      await request('/admin/google-sheet-imports/sync', { method: 'POST' });
      setMessage({ type: 'success', text: 'All active sheet imports synced.' });
      await fetchImports();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setActionLoading(false);
    }
  };

  const renderSummary = (summary) => {
    if (!summary) return 'No sync summary yet';
    return (
      <Summary>
        Rows: {summary.rowsRead || 0}, Approved: {summary.approvedRows || 0}, Created: {summary.usersCreated || 0}, Updated: {summary.usersUpdated || 0}, Skipped: {summary.skippedRows || 0}
        {summary.errors?.length ? <div>Errors: {summary.errors.join('; ')}</div> : null}
      </Summary>
    );
  };

  return (
    <AdminLayout>
      <PageHeader>
        <PageTitle>Google Sheet Imports</PageTitle>
        <Button onClick={handleSyncAll} disabled={actionLoading || !imports.length}>
          Sync All Active
        </Button>
      </PageHeader>

      {message && <Message type={message.type}>{message.text}</Message>}

      <Card>
        <form onSubmit={handleSubmit}>
          <FormGrid>
            <FormGroup>
              <Label>Sheet Link</Label>
              <Input
                type="text"
                value={form.sheetUrl}
                onChange={(event) => setForm({ ...form, sheetUrl: event.target.value })}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Disc %</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={form.discountPercent}
                onChange={(event) => setForm({ ...form, discountPercent: event.target.value })}
                placeholder="25"
                required
              />
            </FormGroup>
            <FormGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
                />
                Active
              </CheckboxLabel>
              <Button type="submit" disabled={actionLoading}>
                {editingId ? 'Save' : 'Add'}
              </Button>
            </FormGroup>
          </FormGrid>
        </form>
        {editingId && (
          <div style={{ marginTop: 12 }}>
            <Button type="button" variant="muted" onClick={resetForm}>Cancel Edit</Button>
          </div>
        )}
      </Card>

      <Card>
        {loading ? (
          <EmptyState>Loading sheet imports...</EmptyState>
        ) : imports.length === 0 ? (
          <EmptyState>Add a Google Sheet link and discount percentage to begin.</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Sheet</Th>
                <Th>Disc %</Th>
                <Th>Status</Th>
                <Th>Last Sync</Th>
                <Th>Summary</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {imports.map((item) => (
                <tr key={item.id}>
                  <Td>
                    <div>{item.sheetName || 'Untitled tab'}</div>
                    <Summary>{item.sheetUrl}</Summary>
                  </Td>
                  <Td>{Number(item.discountPercent || 0).toFixed(2)}%</Td>
                  <Td>
                    <StatusBadge status={item.lastSyncStatus}>
                      {item.isActive ? (item.lastSyncStatus || 'active') : 'inactive'}
                    </StatusBadge>
                  </Td>
                  <Td>{item.lastSyncedAt ? new Date(item.lastSyncedAt).toLocaleString() : 'Never'}</Td>
                  <Td>{renderSummary(item.lastSyncSummary)}</Td>
                  <Td>
                    <Actions>
                      <Button onClick={() => handleSync(item.id)} disabled={actionLoading}>Sync</Button>
                      <Button onClick={() => handleEdit(item)} disabled={actionLoading} variant="muted">Edit</Button>
                      <Button onClick={() => handleDelete(item.id)} disabled={actionLoading} variant="danger">Delete</Button>
                    </Actions>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </AdminLayout>
  );
}
